"use client";

import React from "react";
import { useForm } from "react-hook-form";
import { Form, FormField, FormControl } from "@drpcrd/shared/ui";
import { ArrowUp } from "lucide-react";
import { cn, toast } from "@drpcrd/utils/shared";
import { OnSendAiMessageFn } from "./types";

type ActivityChatInputData = {
  message: string;
};

type ActivityChatInputProps = {
  onSend?: OnSendAiMessageFn;
  placeholder?: string;
  defaultMessage?: string;
  disabled?: boolean;
};

export const ActivityChatInput = ({
  onSend,
  placeholder = "Type here",
  defaultMessage = "",
  disabled,
}: ActivityChatInputProps) => {
  const form = useForm<ActivityChatInputData>({
    defaultValues: { message: defaultMessage },
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = form;

  const onSubmit = async (data: ActivityChatInputData) => {
    const text = data.message.trim();
    if (!text) return;

    try {
      if (onSend) {
        onSend(text, []); // empty array since no files
      }

      form.reset({ message: "" });
    } catch (error) {
      toast("Failed to send message", {
        description: "Something went wrong. Please try again.",
      });
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <div className="mx-auto w-full">
        <div className="max-h-9.5 flex rounded-3xl border border-neutral-500 bg-white p-2">
          <FormField
            control={control}
            name="message"
            render={({ field }) => (
              <FormControl>
                <textarea
                  {...field}
                  aria-label={placeholder}
                  placeholder={placeholder}
                  className="w-full resize-none border-none bg-transparent text-gray-700 placeholder-gray-400 focus:outline-none"
                />
              </FormControl>
            )}
          />
          <div className={cn("flex", "justify-end")}>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              className="bg-primary mt-1.5 flex h-4 w-4 items-center justify-center rounded-full disabled:pointer-events-none disabled:border-neutral-500 disabled:bg-neutral-500 disabled:text-neutral-900 disabled:[&>svg]:text-neutral-900"
              disabled={isSubmitting || disabled}
            >
              <ArrowUp className="h-2 w-2 text-white" />
            </button>
          </div>
        </div>
      </div>
    </Form>
  );
};
